import React, { useState } from 'react';
import { Calendar, Users, X, Clock, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';

/**
 * Add to Plan Modal Component
 * Allows users to add a session to their summer plan with children selection and status
 * 
 * @param {Object} props
 * @param {boolean} props.show - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.session - Session data
 * @param {Array} props.children - Array of children from user profile
 * @param {Function} props.onConfirm - Callback when confirmed: ({ sessionId, children, status }) => void
 */
const AddToPlanModal = ({ 
  show, 
  onClose, 
  session, 
  children = [], 
  onConfirm 
}) => {
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [status, setStatus] = useState('considering');
  
  if (!show || !session) return null;

  const handleChildToggle = (childId) => {
    setSelectedChildren(prev =>
      prev.includes(childId)
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
  };
  
  const handleConfirm = () => {
    if (selectedChildren.length === 0) return;
    
    onConfirm({
      sessionId: session.id || session.place_id,
      children: selectedChildren,
      status: status
    });
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return 'Dates not available';
    try {
      // Parse dates as local dates to avoid timezone conversion issues
      const parseDate = (dateStr) => {
        if (!dateStr) return null;
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(year, month - 1, day);
        }
        return new Date(dateStr);
      };
      
      const start = parseDate(startDate);
      const end = parseDate(endDate);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'Invalid dates';
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    } catch (error) {
      return 'Invalid dates';
    }
  };

  // Get session dates from various possible locations
  const sessionStartDate = session.user_camp_start_date || 
                          session.startDate || 
                          session.start_date || 
                          session.camp_start_date ||
                          (session.program_data?.user_camp_start_date) ||
                          (session.program_data?.start_date);
  
  const sessionEndDate = session.user_camp_end_date || 
                        session.endDate || 
                        session.end_date || 
                        session.camp_end_date ||
                        (session.program_data?.user_camp_end_date) ||
                        (session.program_data?.end_date);

  const sessionName = session.session || 
                     session.sessionName || 
                     session.name ||
                     (session.program_data?.session) ||
                     'Session';

  const sessionCost = session.cost || 
                     session.price || 
                     session.session_price ||
                     (session.program_data?.cost) ||
                     (session.program_data?.price);

  return (
    <Dialog open={show} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add to Your Summer Plan</DialogTitle>
          <DialogDescription>
            Select which children are attending and mark the status
          </DialogDescription>
        </DialogHeader>

        {/* Session Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-2">{sessionName}</h4>
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
            <Calendar className="w-4 h-4" />
            <span>{formatDateRange(sessionStartDate, sessionEndDate)}</span>
          </div>
          {sessionCost && (
            <div className="text-sm font-semibold text-purple-700 mt-1">
              ${parseFloat(sessionCost).toFixed(2)}
            </div>
          )}
        </div>

        {/* Children Selection */}
        <div className="space-y-3 mb-6">
          <Label className="text-sm font-semibold text-gray-900">
            Which children are attending?
          </Label>
          {children.length === 0 ? (
            <p className="text-sm text-gray-500">
              No children found in your profile. Add children in your profile settings.
            </p>
          ) : (
            <div className="space-y-2">
              {children.map(child => (
                <div key={child.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`child-${child.id}`}
                    checked={selectedChildren.includes(child.id)}
                    onCheckedChange={() => handleChildToggle(child.id)}
                  />
                  <Label
                    htmlFor={`child-${child.id}`}
                    className="text-sm font-normal cursor-pointer flex-1"
                  >
                    {child.name}
                  </Label>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Status Selection */}
        <div className="space-y-3 mb-6">
          <Label className="text-sm font-semibold text-gray-900">Status</Label>
          <div className="flex gap-3">
            <Button
              type="button"
              variant={status === 'considering' ? 'default' : 'outline'}
              className={`flex-1 ${status === 'considering' ? 'bg-yellow-500 hover:bg-yellow-600' : ''}`}
              onClick={() => setStatus('considering')}
            >
              <Clock className="w-4 h-4 mr-2" />
              Considering
            </Button>
            <Button
              type="button"
              variant={status === 'booked' ? 'default' : 'outline'}
              className={`flex-1 ${status === 'booked' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
              onClick={() => setStatus('booked')}
            >
              <Check className="w-4 h-4 mr-2" />
              Booked
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Mark as "Booked" if you've already registered for this session
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            className="flex-1 bg-purple-600 hover:bg-purple-700"
            disabled={selectedChildren.length === 0}
          >
            Add to Plan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddToPlanModal;
